/**
 * Teaching the catalogue what a customer calls an item.
 *
 * ---- What a tag IS ----
 *
 * `Item.tags` is a child table (`Item Tags`) of free text. A real one already on the bench:
 *
 *     Item 4701060006  tag: "3M DOUBLE SIDED TAPE FOAM TYPE, SIZE : 1 INCH"
 *
 * — the CUSTOMER's wording, stored against the item it actually means. So when the matcher gets a
 * line wrong and the purchaser corrects it by searching the catalogue, the correction is worth
 * keeping: the same customer writes the same thing next month, and the tag is what lets the search
 * find it. Without this every correction is made once and forgotten.
 *
 * ---- The wire, measured 2026-09-11 ----
 *
 *     POST rfq.update_item_tags  {items: [{item_code, required_item, add_to_tag}]}  -> 200 {}
 *
 * `items` is required. A BARE array answers `500 AttributeError: 'list' object has no attribute
 * 'get'`, so the envelope is not optional — and the method returns `{}` either way, which means
 * nothing here can confirm a tag was written from the response alone. Verified by reading the
 * child table back instead.
 *
 * `add_to_tag: 0` is accepted and does NOTHING — the tag stays. Removal is not implemented
 * server-side, so this module only ever adds, rather than offering an un-tag that silently fails.
 */

import { useMutation } from '@tanstack/react-query'
import { callPost } from './bosun'
import type { BosunApiError } from './errors'

export interface ItemTagWrite {
  /** The catalogue item the purchaser corrected the line TO. */
  item_code: string
  /** The customer's own wording for it — what the tag records. */
  required_item: string
}

interface WireItemTag extends ItemTagWrite {
  /**
   * Always 1, and typed as the literal so it cannot become anything else.
   *
   * Confirmed with Akhil 2026-09-11: the app only ever ADDS. Removal is a Desk job precisely
   * because `add_to_tag: 0` answers `200` and does nothing (`D32`) — offering it here would be a
   * control that reports success for work the server never did.
   */
  add_to_tag: 1
}

/** The only part of a line this needs: what the customer wrote, and what it was matched to before. */
interface CorrectedLine {
  raw_text: string
  item_code: string | null
}

/**
 * The tags earned by matching `lines` onto `itemCode`.
 *
 * A line that ALREADY held this item is skipped: the purchaser re-confirmed a match rather than
 * correcting one, and the search already knows that wording points here. An UNMATCHED line
 * (`item_code: null`) is kept — the matcher offered nothing, a human decided, and that decision is
 * exactly as worth learning as an outright correction.
 */
export function tagWritesForCorrection(lines: readonly CorrectedLine[], itemCode: string): ItemTagWrite[] {
  return lines
    .filter((line) => line.item_code !== itemCode)
    .map((line) => ({ item_code: itemCode, required_item: line.raw_text }))
}

/**
 * The wire body, with the rows that would teach nothing dropped.
 *
 * A blank `required_item` is not a synonym, and a tag equal to the item's own code teaches the
 * search something it already knows. Both would just grow the table.
 */
export function toItemTagsBody(writes: readonly ItemTagWrite[]): { items: WireItemTag[] } {
  const items: WireItemTag[] = []
  const seen = new Set<string>()

  for (const write of writes) {
    const itemCode = write.item_code.trim()
    const required = write.required_item.trim()
    if (itemCode === '' || required === '') continue
    if (required.toLowerCase() === itemCode.toLowerCase()) continue

    // One row per (item, wording) — a bulk match of five lines onto one item, all with the same
    // customer text, is one tag and not five identical ones.
    const key = `${itemCode} ${required.toLowerCase()}`
    if (seen.has(key)) continue
    seen.add(key)

    items.push({ item_code: itemCode, required_item: required, add_to_tag: 1 })
  }

  return { items }
}

/** Longest wording a toast can carry before it starts wrapping over the screen. */
const MAX_WORDING = 50

/**
 * What to tell the purchaser once a tag is saved, or `null` when nothing was.
 *
 * They asked to be told (Akhil, 2026-09-11): the tag is invisible otherwise — it changes nothing
 * on this screen and only shows up months later as a better match — so without a word here the
 * correction looks like it went nowhere. The message names the wording, so it is checkable, and
 * says what the tag BUYS, so it reads as a result rather than a log line.
 */
export function describeTagged(rows: readonly ItemTagWrite[]): string | null {
  if (rows.length === 0) return null

  const itemCode = rows[0].item_code
  if (rows.length > 1) {
    return `🏷 Tagged ${rows.length} customer names to ${itemCode} — they will match this item next time`
  }

  const wording = rows[0].required_item
  const shown = wording.length > MAX_WORDING ? `${wording.slice(0, MAX_WORDING - 1)}…` : wording
  return `🏷 Tagged “${shown}” to ${itemCode} — it will match this item next time`
}

/**
 * Record the corrections, resolving to the rows actually written — `describeTagged` reports THOSE
 * rather than what the caller offered, so a batch whose rows were all dropped as duplicates says
 * nothing instead of claiming a tag that never went out.
 *
 * Still non-blocking at the call sites: a tag is a side benefit of matching a line, and failing to
 * save one must never fail, block or undo the match itself — which is the thing the purchaser
 * actually asked for. It is now merely LOUD about both outcomes rather than silent.
 */
export function useUpdateItemTags(options?: {
  onSuccess?: (rows: ItemTagWrite[]) => void
  onError?: (error: BosunApiError) => void
}) {
  return useMutation<ItemTagWrite[], BosunApiError, ItemTagWrite[]>({
    mutationFn: async (writes) => {
      const { items } = toItemTagsBody(writes)
      if (items.length === 0) return []
      await callPost('rfq', 'update_item_tags', { items })
      return items.map(({ item_code, required_item }) => ({ item_code, required_item }))
    },
    onSuccess: (rows) => options?.onSuccess?.(rows),
    onError: (error) => options?.onError?.(error),
  })
}
